import storage from '@/service/storage-service'
import { isEmbedded } from './embed-mode'
import center from './center'

function sourceWindow () { return isEmbedded ? window.parent : window.opener }

const READY_MESSAGE = 'JUKUN_WEB_PLATFORM_TOKEN_READY'
const TOKEN_MESSAGE = 'JUKUN_WEB_PLATFORM_TOKEN'
const REFRESH_MESSAGE = 'JUKUN_WEB_PLATFORM_TOKEN_REFRESH'
const ACCEPTED_MESSAGE = 'JUKUN_WEB_PLATFORM_TOKEN_ACCEPTED'
const REJECTED_MESSAGE = 'JUKUN_WEB_PLATFORM_TOKEN_REJECTED'
const BRIDGE_KEY = 'platform-token-bridge'
const APPLICATION_CODE = process.env.APP_PLATFORM_APPLICATION_CODE || 'JUKUN_MONITOR'
const PLATFORM_AUTH_BASE_URL = String(process.env.APP_PLATFORM_AUTH_BASE_URL || '/api/auth').replace(/\/+$/, '')
const defaultTrustedOrigins = [
  'http://127.0.0.1:8081',
  'http://localhost:8081',
  'http://127.0.0.1:5175',
  'http://localhost:5175',
  'https://mon.jukun.tech',
  'https://platform.jukun.tech'
]
const configuredTrustedOrigins = String(process.env.APP_TRUSTED_SSO_ORIGINS || '')
  .split(',')
  .map(value => value.trim())
  .filter(Boolean)
const trustedOrigins = defaultTrustedOrigins.concat(configuredTrustedOrigins)
  .filter((value, index, values) => values.indexOf(value) === index)

let refreshTimer = null
let embeddedPort = null
let embeddedRoute = { path: '', projectId: '' }
export function reportEmbeddedRoute (path, projectId) {
  embeddedRoute = { path, projectId: /^\d+$/.test(String(projectId || '')) ? String(projectId) : '' }
  const current = bridge()
  if (isEmbedded && embeddedPort && current) {
    embeddedPort.postMessage({ type: 'JUKUN_BOARD_ROUTE', nonce: current.nonce, ...embeddedRoute })
  }
}
const pendingRefresh = new Map()

function receiveEmbeddedToken (query) {
  if (!query.handoff || !trusted(query.sourceOrigin)) return Promise.reject(new Error('看板交接来源无效'))
  storage.setSession(BRIDGE_KEY, { nonce: query.handoff, sourceOrigin: query.sourceOrigin })
  if (embeddedPort) embeddedPort.close()
  const channel = new MessageChannel()
  embeddedPort = channel.port1
  return new Promise((resolve, reject) => {
    let initialized = false
    const timeout = window.setTimeout(() => {
      channel.port1.close()
      reject(new Error('等待看板令牌超时，请重新加载'))
    }, 15000)
    channel.port1.onmessage = event => {
      const data = event.data
      if (!data || data.nonce !== query.handoff) return
      if (data.type === 'JUKUN_BOARD_HOME') {
        center.$emit('board-home')
        return
      }
      if (data.type !== TOKEN_MESSAGE) return
      if (typeof data.accessToken !== 'string' || !data.accessToken) return
      savePlatformAccessToken(data.accessToken, data.permissionCodes)
      if (data.requestId && pendingRefresh.has(data.requestId)) {
        pendingRefresh.get(data.requestId)(data.accessToken)
        pendingRefresh.delete(data.requestId)
      }
      if (!initialized) {
        initialized = true
        window.clearTimeout(timeout)
        resolve(data.accessToken)
      }
    }
    // 端口只交给明确来源的直接父页；父页验证 iframe 窗口、来源和 nonce 后持有端口。
    window.parent.postMessage({ type: READY_MESSAGE, nonce: query.handoff }, query.sourceOrigin, [channel.port2])
  })
}

function trusted (origin) {
  return trustedOrigins.indexOf(origin) !== -1
}

function bridge () {
  return storage.getSession(BRIDGE_KEY)
}

export function savePlatformAccessToken (accessToken, permissionCodes) {
  const current = storage.getUser() || {}
  const payload = jwtPayload(accessToken)
  storage.setUser(Object.assign({}, current, {
    authMode: 'platform',
    token: accessToken.indexOf('Bearer ') === 0 ? accessToken : `Bearer ${accessToken}`,
    platformScopes: Array.isArray(payload.scp) ? payload.scp : [],
    platformPermissionCodes: Array.isArray(permissionCodes) ? permissionCodes : []
  }))
  scheduleRefresh(accessToken)
}

function jwtPayload (accessToken) {
  try {
    const raw = accessToken.replace(/^Bearer\s+/i, '').split('.')[1]
    var normalized = raw.replace(/-/g, '+').replace(/_/g, '/')
    while (normalized.length % 4) normalized += '='
    return JSON.parse(decodeURIComponent(escape(window.atob(normalized))))
  } catch (error) {
    return {}
  }
}

function jwtExpiresAt (accessToken) {
  return Number(jwtPayload(accessToken).exp || 0) * 1000
}

function scheduleRefresh (accessToken) {
  if (refreshTimer) window.clearTimeout(refreshTimer)
  const expiresAt = jwtExpiresAt(accessToken)
  if (!expiresAt) return
  const delay = Math.max(5000, expiresAt - Date.now() - 60000)
  refreshTimer = window.setTimeout(() => {
    refreshPlatformToken().catch(() => undefined)
  }, delay)
}

async function platformAuthRequest (path, init) {
  const response = await window.fetch(`${PLATFORM_AUTH_BASE_URL}${path}`, Object.assign({
    credentials: 'include',
    headers: { Accept: 'application/json' }
  }, init || {}, {
    headers: Object.assign({ Accept: 'application/json' }, (init && init.headers) || {})
  }))
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(payload.message || payload.detail || payload.error || '中台认证失败')
  }
  return payload
}

async function requestStandaloneAccessToken () {
  const token = await platformAuthRequest('/access-tokens', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ applicationCode: APPLICATION_CODE })
  })
  if (!token || !token.accessToken) throw new Error('中台应用令牌初始化失败')
  const response = await window.fetch(`${PLATFORM_AUTH_BASE_URL.replace(/\/auth$/, '/portal')}/applications/${APPLICATION_CODE}/permission-context`, { credentials: 'include', headers: { Accept: 'application/json' } })
  const permissions = response.ok ? await response.json() : {}
  savePlatformAccessToken(token.accessToken, permissions.applicationCode === APPLICATION_CODE ? permissions.permissionCodes : [])
  return token
}

export async function loginStandalonePlatform (identifier, password) {
  await platformAuthRequest('/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password })
  })
  return requestStandaloneAccessToken()
}

export async function logoutPlatformSession () {
  if (isEmbedded) {
    reportEmbeddedSession(false)
    storage.clearSession()
    if (refreshTimer) window.clearTimeout(refreshTimer)
    return
  }
  try {
    await platformAuthRequest('/session', { method: 'DELETE' })
  } finally {
    if (refreshTimer) window.clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

function requestId () {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function receiveInitialPlatformToken (query) {
  if (isEmbedded) return receiveEmbeddedToken(query)
  const nonce = query.handoff
  const sourceOrigin = query.sourceOrigin
  let invalidReason = ''
  if (!nonce) invalidReason = '中台令牌交接标识缺失'
  else if (!sourceOrigin) invalidReason = '中台令牌交接来源缺失'
  else if (!trusted(sourceOrigin)) invalidReason = `中台令牌交接来源不受信任：${sourceOrigin}`
  else if (!sourceWindow()) invalidReason = '中台令牌交接窗口已断开'
  if (invalidReason) {
    if (sourceWindow() && sourceOrigin && trusted(sourceOrigin)) {
      sourceWindow().postMessage({
        type: REJECTED_MESSAGE,
        nonce,
        error: invalidReason
      }, sourceOrigin)
    }
    return Promise.reject(new Error(`${invalidReason}，请从监测管理后台重新进入`))
  }

  storage.setSession(BRIDGE_KEY, { nonce, sourceOrigin })
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error('等待中台应用令牌超时，请重新进入'))
    }, 15000)
    const cleanup = () => {
      window.clearTimeout(timeout)
      window.removeEventListener('message', onMessage)
    }
    const onMessage = event => {
      if (event.source !== sourceWindow() || event.origin !== sourceOrigin
          || !event.data || event.data.type !== TOKEN_MESSAGE
          || event.data.nonce !== nonce || event.data.requestId) return
      cleanup()
      if (!event.data.accessToken) {
        reject(new Error(event.data.error || '中台应用令牌为空'))
        return
      }
      savePlatformAccessToken(event.data.accessToken, event.data.permissionCodes)
      if (!isEmbedded) sourceWindow().postMessage({ type: ACCEPTED_MESSAGE, nonce }, sourceOrigin)
      resolve(event.data.accessToken)
    }
    window.addEventListener('message', onMessage)
    sourceWindow().postMessage({ type: READY_MESSAGE, nonce }, sourceOrigin)
  })
}

export function refreshPlatformToken () {
  if (isEmbedded) {
    const current = bridge()
    if (!embeddedPort || !current) return Promise.reject(new Error('看板会话已断开'))
    const id = requestId()
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        pendingRefresh.delete(id)
        reportEmbeddedSession(false)
        reject(new Error('看板令牌刷新超时'))
      }, 15000)
      pendingRefresh.set(id, token => { window.clearTimeout(timeout); resolve(token) })
      embeddedPort.postMessage({ type: REFRESH_MESSAGE, nonce: current.nonce, requestId: id })
    })
  }
  const currentBridge = bridge()
  if (!currentBridge || !trusted(currentBridge.sourceOrigin) || !sourceWindow()) {
    if (isEmbedded) return Promise.reject(new Error('嵌入会话已失效，请从看板重新加载'))
    return requestStandaloneAccessToken().then(token => token.accessToken)
  }
  const id = requestId()
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error('中台应用令牌刷新超时'))
    }, 15000)
    const cleanup = () => {
      window.clearTimeout(timeout)
      window.removeEventListener('message', onMessage)
    }
    const onMessage = event => {
      if (event.source !== sourceWindow() || event.origin !== currentBridge.sourceOrigin
          || !event.data || event.data.type !== TOKEN_MESSAGE
          || event.data.nonce !== currentBridge.nonce || event.data.requestId !== id) return
      cleanup()
      if (!event.data.accessToken) {
        reject(new Error(event.data.error || '中台应用令牌刷新失败'))
        return
      }
      savePlatformAccessToken(event.data.accessToken, event.data.permissionCodes)
      resolve(event.data.accessToken)
    }
    window.addEventListener('message', onMessage)
    sourceWindow().postMessage({
      type: REFRESH_MESSAGE,
      nonce: currentBridge.nonce,
      requestId: id
    }, currentBridge.sourceOrigin)
  })
}

export function resumePlatformTokenRefresh () {
  const current = storage.getUser()
  if (current && current.authMode === 'platform' && current.token) {
    scheduleRefresh(current.token)
  }
}

export function canViewDeviceControl () {
  const user = storage.getUser() || {}
  const codes = user.platformPermissionCodes || []
  return codes.indexOf('JUKUN_MONITOR_DEVICE_CONTROL_PAGE') !== -1 &&
    codes.indexOf('JUKUN_MONITOR_DEVICE_CONTROL_EXECUTE') !== -1
}

// 嵌入态等业务身份初始化成功才向父页面确认，不能把拿到 Token 当作加载成功。
export function reportEmbeddedSession (success) {
  const current = bridge()
  if (!isEmbedded || !current || !trusted(current.sourceOrigin)) return
  if (embeddedPort) embeddedPort.postMessage({ type: success ? ACCEPTED_MESSAGE : REJECTED_MESSAGE,
    nonce: current.nonce })
  if (success) reportEmbeddedRoute(embeddedRoute.path, embeddedRoute.projectId)
}



// WEBPACK FOOTER //
// ./src/service/platform-token-bridge.js